import React, { useRef, useState } from "react";
import { StickyCta } from "./StepIntro";
import { PHOTO_CHECK_COPY } from "../../lib/content";
import { compressImage, isImageFile } from "../../lib/imageCompress";
import { Camera, ImageIcon, RefreshCw } from "lucide-react";

/**
 * Photo check step — upload OR camera capture (mobile), preview, then
 * "Analyze" or "Skip". Client-side compression keeps the upload <6MB.
 * On mobile, `capture="environment"` on a file input triggers the camera.
 */
export function StepPhotoCheck({ onAnalyze, onSkip, submitting, error, catName }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [localErr, setLocalErr] = useState(null);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    const handleFiles = async (list) => {
        setLocalErr(null);
        const raw = list?.[0];
        if (!raw) return;
        if (!isImageFile(raw)) {
            setLocalErr(
                "That doesn't look like a photo — try a JPG, PNG or WEBP.",
            );
            return;
        }
        try {
            const compressed = await compressImage(raw);
            if (compressed.size > 6 * 1024 * 1024) {
                setLocalErr(PHOTO_CHECK_COPY.tooLarge);
                return;
            }
            setFile(compressed);
            setPreview(URL.createObjectURL(compressed));
        } catch {
            setLocalErr(PHOTO_CHECK_COPY.fallbackError);
        }
    };

    const submit = () => file && onAnalyze(file);

    return (
        <div className="pb-48" data-testid="step-photo-check">
            <div className="mt-2 space-y-2">
                <span className="eyebrow" data-testid="photo-eyebrow">
                    {PHOTO_CHECK_COPY.eyebrow}
                </span>
                <h1 className="font-display text-[1.9rem] font-medium leading-tight text-meow-charcoal">
                    {PHOTO_CHECK_COPY.title}
                </h1>
                <p className="text-[14px] leading-relaxed text-meow-muted">
                    {PHOTO_CHECK_COPY.instructions}
                    {catName ? (
                        <>
                            {" "}
                            Ideally a clear shot of{" "}
                            <span className="font-medium text-meow-charcoal">{catName}</span>.
                        </>
                    ) : null}
                </p>
            </div>

            <div className="mt-5">
                {preview ? (
                    <div className="relative overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_8px_30px_rgba(42,42,42,0.06)]">
                        <img
                            src={preview}
                            alt="Your cat"
                            className="h-64 w-full rounded-xl object-cover"
                            data-testid="photo-preview"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setFile(null);
                                setPreview(null);
                            }}
                            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-meow-charcoal shadow"
                            data-testid="photo-change-btn"
                        >
                            <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
                            {PHOTO_CHECK_COPY.changeLabel}
                        </button>
                    </div>
                ) : (
                    <div
                        className="rounded-2xl border border-dashed border-meow-teal/40 bg-white/60 p-6 text-center"
                        data-testid="photo-empty"
                    >
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-meow-icon text-meow-teal">
                            <Camera className="h-6 w-6" strokeWidth={1.75} aria-hidden />
                        </div>
                        <p className="mt-3 text-[13px] leading-relaxed text-meow-muted">
                            A well-lit, whole-body shot works best.
                        </p>
                        <div className="mt-5 grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                className="meow-btn-secondary text-[14px]"
                                onClick={() => cameraRef.current?.click()}
                                data-testid="photo-camera-btn"
                            >
                                <Camera className="mr-2 h-4 w-4" strokeWidth={2} />
                                Camera
                            </button>
                            <button
                                type="button"
                                className="meow-btn-secondary text-[14px]"
                                onClick={() => fileRef.current?.click()}
                                data-testid="photo-gallery-btn"
                            >
                                <ImageIcon className="mr-2 h-4 w-4" strokeWidth={2} />
                                Gallery
                            </button>
                        </div>
                    </div>
                )}

                <input
                    ref={cameraRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="photo-camera-input"
                />
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="photo-gallery-input"
                />

                {(localErr || error) && (
                    <p
                        className="mt-3 rounded-xl bg-meow-emergency px-3 py-2 text-[13px] leading-snug text-meow-red"
                        data-testid="photo-error"
                    >
                        {localErr || error}
                    </p>
                )}

                <p className="mt-4 text-[12px] leading-relaxed text-meow-muted">
                    {PHOTO_CHECK_COPY.footerDisclaimer}
                </p>
            </div>

            <StickyCta>
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        className="meow-btn-primary w-full"
                        onClick={submit}
                        disabled={!file || submitting}
                        data-testid="photo-analyze-btn"
                    >
                        {submitting
                            ? PHOTO_CHECK_COPY.analyzing
                            : PHOTO_CHECK_COPY.analyzeLabel}
                    </button>
                    <button
                        type="button"
                        className="meow-btn-ghost mx-auto text-[14px]"
                        onClick={onSkip}
                        disabled={submitting}
                        data-testid="photo-skip-btn"
                    >
                        {PHOTO_CHECK_COPY.skipLabel}
                    </button>
                </div>
            </StickyCta>
        </div>
    );
}
